import { useEffect, useRef, useState } from "react";
import { needsYear, paymentMonthLabel, type PointHistoryItemView } from "@makit/shared";

import { Button, FilterChip, Skeleton } from "../../components/primitives";
import { SettingGroup, SettingRow } from "../../components/settings";
import { usePointBalance, usePointHistory } from "../../lib/points-api";
import {
  EMPTY_HISTORY_NOTE,
  NEGATIVE_BALANCE_NOTE,
  SHOW_NEGATIVE_BALANCE_NOTE,
  POINT_FILTERS,
  SHORTFALL_LABEL,
  balanceText,
  groupPointsByMonth,
  matchesPointFilter,
  pointRowDetail,
  pointRowTitle,
  shortfallText,
  signedPoints,
  type PointFilter,
} from "./point-labels";

/** 접힌 동안 보이는 최근 줄 수. 목업 `W-70` 이 둘을 그렸다. */
const RECENT_ROWS = 2;

/** `W-70` 마이페이지의 「포인트」 칸. **읽기 전용이다.** 청구 내역(`청구 내역` 머리) 바로 위에 둔다.
 *
 * 목업 `W-70`(2026-10-25 둘째 회차)과 PRD `7.5.9` 「포인트의 낱말」 · `13.1` O-88(2026-10-27 `design-manager`):
 *   - 머리 `포인트`, 굵은 줄 `남은 포인트`, 최근 줄 둘, 그리고 `포인트 내역 전체 · 충전 · 씀 · 돌아옴 ⌄`
 *     (제자리 펼침. 펼치면 칩 `전체 · 충전 · 씀 · 돌아옴` 과 월 머리글이 선다. 앱 `A-43d` 와 같은 갈래)
 *   - 줄: `충전 · App Store` / `+₩10,000` / `09-06 15:10 · 앱에서 충전`, `무광 세라믹 머그 · 씀` / `−₩3,000` / `09-06 09:43 · 6장`
 *
 * ⛔ **이 칸에 `포인트 충전` · `충전 환불 요청` 이 없다.** 웹은 포인트를 팔지 않는다(사용자 정정 2026-10-25).
 * ⛔ **칸 아래 hint 두 줄을 싣지 않는다**(2026-10-27 `design-manager`). 첫 줄의 「여기서는 남은 포인트를 결제에
 *    쓰실 수 있습니다」는 웹 포인트 결제(`W-26`, O-89)가 없어 거짓이고, 「충전과 충전 환불은 makit 앱에서 합니다」도
 *    앱 충전(`A-49`, O-84)이 아직 없다. 둘째 줄은 법무 판정(O-90) 전의 환불 불가 문장이다.
 * ⛔ 씀 줄 꼬리 `나머지 ₩1,000은 신한 1234` 는 웹에서 섞어 내는 길이 없어 생기지 않는다.
 *
 * 마운트: `apps/web/src/screens/Account.tsx` 의 `<h4 className="hist-h">청구 내역</h4>` 바로 앞에
 * `<PointsSection />` 한 줄.
 */
export function PointsSection() {
  const balance = usePointBalance();
  const history = usePointHistory();
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<PointFilter>("ALL");

  const items: PointHistoryItemView[] = history.data?.pages.flatMap((page) => page.items) ?? [];
  // 연도는 열 단위로 켠다(PRD 7.5.13 규칙 4). 한 줄이라도 올해가 아니면 그 열 전부.
  const withYear = needsYear(items.map((item) => item.occurredAt));
  /** 모자란 포인트. 남은 포인트가 0 아래일 때만 값이 있다(앱 `A-43f` 와 같은 두 줄). */
  const shortfall = balance.data ? shortfallText(balance.data.balance.balance) : null;

  return (
    <>
      <h4 className="hist-h">포인트</h4>
      {balance.isPending ? (
        <div role="status" aria-live="polite" style={{ display: "grid", gap: 7, marginBottom: 14 }}>
          <span className="sr-only">포인트를 불러오는 중입니다</span>
          <Skeleton width="70%" />
          <Skeleton width="52%" />
        </div>
      ) : balance.isError ? (
        <LoadFailed onRetry={() => void balance.refetch()} />
      ) : (
        <div style={{ marginBottom: 14 }}>
          <SettingGroup bill>
            {/* ⭐ **모자란 동안은 줄이 둘이다**(앱 `A-43f` 와 같다). 남은 포인트는 `₩0`, 모자란 몫은 그 아래 굵은 줄.
                ⛔ 음수를 남은 포인트로 적지 않는다. 셀러가 빚을 남은 돈으로 읽는다. */}
            {shortfall === null ? (
              <SettingRow tone="total" label="남은 포인트" value={balanceText(balance.data.balance.balance)} />
            ) : (
              <>
                <SettingRow label="남은 포인트" value={balanceText(balance.data.balance.balance)} />
                <SettingRow tone="total" label={SHORTFALL_LABEL} value={shortfall} />
              </>
            )}
            {/* 법무 `L-F78`-4 전에는 꺼 둔다(`SHOW_NEGATIVE_BALANCE_NOTE`). */}
            {SHOW_NEGATIVE_BALANCE_NOTE && balance.data.balance.balance < 0 ? (
              <p className="hint" style={{ margin: "6px 0" }}>
                {NEGATIVE_BALANCE_NOTE}
              </p>
            ) : null}
            {history.isPending ? (
              <div role="status" aria-live="polite" style={{ display: "grid", gap: 7, padding: "8px 0" }}>
                <span className="sr-only">포인트 내역을 불러오는 중입니다</span>
                <Skeleton width="62%" />
                <Skeleton width="40%" />
              </div>
            ) : history.isError && items.length === 0 ? (
              <LoadFailed inline onRetry={() => void history.refetch()} />
            ) : items.length === 0 ? (
              <p className="hint" style={{ margin: "8px 0" }}>
                {EMPTY_HISTORY_NOTE}
              </p>
            ) : expanded ? (
              <ExpandedHistory
                items={items}
                filter={filter}
                onFilter={setFilter}
                withYear={withYear}
                hasMore={history.hasNextPage}
                loadingMore={history.isFetchingNextPage}
                moreFailed={history.isFetchNextPageError}
                onMore={() => void history.fetchNextPage()}
              />
            ) : (
              items.slice(0, RECENT_ROWS).map((item) => (
                <PointRow key={item.entryId} item={item} withYear={withYear} />
              ))
            )}
            {/* 빈 내역에서는 펼침 줄을 두지 않는다. 펼칠 것이 없는 `⌄` 는 누를 곳 없는 꺾쇠다. */}
            {items.length > 0 ? (
              <SettingRow
                label="포인트 내역 전체 · 충전 · 씀 · 돌아옴"
                action={expanded ? undefined : "sheet"}
                value={expanded ? <span aria-hidden="true">⌃</span> : undefined}
                onClick={() => setExpanded(!expanded)}
              />
            ) : null}
          </SettingGroup>
        </div>
      )}
    </>
  );
}

function LoadFailed({ onRetry, inline = false }: { onRetry: () => void; inline?: boolean }) {
  return (
    <p className="hint" style={inline ? { margin: "8px 0" } : { margin: "0 0 14px" }}>
      <span className="l1">
        <b>포인트 내역을 지금 불러올 수 없어요.</b> 연결이 끊겼거나 잠깐 답이 오지 않았습니다.
      </span>
      <span className="l2">
        <Button label="다시 시도" small onClick={onRetry} />
      </span>
    </p>
  );
}

function PointRow({ item, withYear }: { item: PointHistoryItemView; withYear: boolean }) {
  // 웹 줄에는 `›` 가 없다(목업 `W-70`). 작업의 정산 명세는 아래 청구 내역 줄이 제자리에서 펼친다.
  return (
    <div className="payrow" style={{ cursor: "default" }}>
      <span className="pn">{pointRowTitle(item)}</span>
      <span className="pa mono">{signedPoints(item.amount)}</span>
      <span className="pm mono">{pointRowDetail(item, { year: withYear, surface: "web" })}</span>
      <span className="ps" />
    </div>
  );
}

function ExpandedHistory({
  items,
  filter,
  onFilter,
  withYear,
  hasMore,
  loadingMore,
  moreFailed,
  onMore,
}: {
  items: PointHistoryItemView[];
  filter: PointFilter;
  onFilter: (next: PointFilter) => void;
  withYear: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  moreFailed: boolean;
  onMore: () => void;
}) {
  const shown = items.filter((item) => matchesPointFilter(item, filter));
  const groups = groupPointsByMonth(shown);
  const sentinel = useRef<HTMLDivElement>(null);

  // **다음 쪽은 목록 끝이 화면에 들어왔을 때만 부른다.** 버튼을 두지 않은 것은 그 버튼의 낱말이 용어 표에
  // 없어서다. 실패한 뒤에는 저절로 다시 부르지 않는다(아래 `다시 시도` 가 부른다).
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || loadingMore || moreFailed) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onMore();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, moreFailed, onMore]);

  return (
    <div>
      <div className="fchips" role="group" aria-label="포인트 내역 갈래" style={{ margin: "8px 0" }}>
        {POINT_FILTERS.map((option) => (
          <FilterChip
            key={option.key}
            label={option.label}
            on={filter === option.key}
            onClick={() => onFilter(option.key)}
          />
        ))}
      </div>
      {shown.length === 0 && !hasMore ? (
        <p className="hint">조건에 맞는 줄이 없습니다.</p>
      ) : (
        groups.map((group) => (
          <div key={group.yearMonth || "unknown"}>
            {/* 월 머리글에 합계를 두지 않는다(목업 `A-43d`). 충전과 씀을 한 숫자로 더하면 뜻이 없다. */}
            {group.yearMonth ? (
              <div className="paymonth">
                <span className="pmm">{paymentMonthLabel(group.yearMonth)}</span>
              </div>
            ) : null}
            {group.items.map((item) => (
              <PointRow key={item.entryId} item={item} withYear={withYear} />
            ))}
          </div>
        ))
      )}
      {loadingMore ? (
        <div role="status" aria-live="polite" style={{ display: "grid", gap: 7, padding: "8px 0" }}>
          <span className="sr-only">포인트 내역을 불러오는 중입니다</span>
          <Skeleton width="62%" />
        </div>
      ) : moreFailed ? (
        <LoadFailed inline onRetry={onMore} />
      ) : null}
      <div ref={sentinel} aria-hidden="true" style={{ height: 1 }} />
    </div>
  );
}
